import React, { useState, useEffect } from 'react';
import { useLeftSidebarState } from '@atlassian/jira-layout-controller';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	getApplicationForProject,
	getEdition,
	STANDARD_EDITION,
} from '@atlassian/jira-shared-types';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import {
	isUpgradedBasedOnTraits,
	useHasBeenUpgradedFromFree,
	useShouldShowSpotlightOnLand,
} from '../services';
import { useEPMessagesForCoordination } from '../services/coordination-client';
import { getModalViewedProperty } from '../services/user-properties';
import {
	projectTypeToApplicationKey,
	fireUpgradeOnboardingExperimentExposure,
	isOnVariationForUpgradeOnboarding,
} from '../utils';
import { useRefreshFlag } from './refresh-flag';
import { SpotlightPill } from './spotlight';
import type { Props } from './types';
import UpgradeOnboardingModal from './upgrade-onboarding-modal';

export const ExperimentContainer = ({
	projectType,
	addPaymentMethodRef,
	transactionAccountId,
}: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isSpotlightActive, setIsSpotlightActive] = useState(false);
	const { cloudId, atlassianAccountId, appEditions } = useTenantContext();
	const { showFlag } = useRefreshFlag(projectType);

	const isVariation = isOnVariationForUpgradeOnboarding(projectType);

	const editionFromTenantContext = getEdition(getApplicationForProject(projectType), appEditions);
	const product = projectTypeToApplicationKey(projectType);

	const isUpgradedInCurrentSession = useHasBeenUpgradedFromFree({
		currentEdition: editionFromTenantContext,
		product,
		shouldCheckImmediately: true,
	});

	const {
		hasMessageStarted: isEnrolledToEPMessage,
		setEpMessageStarted,
		stopMessage,
	} = useEPMessagesForCoordination(projectType);

	// determine if the user is eligible to see the modal regardless if we are on control or variation
	const isEligible = addPaymentMethodRef && isEnrolledToEPMessage;
	// only actually show the modal if we are on variation
	const showModal = isEligible && isVariation;
	const { isCollapsed } = useLeftSidebarState();

	// the keyboard event expands the sidebar if it is collapsed so that the spotlight can correctly highlight the upgrade pill
	// updating the sidebar context did not expand/collapse the sidebar so we went this route
	useEffect(() => {
		isCollapsed && showModal && document.dispatchEvent(new KeyboardEvent('keydown', { key: '[' }));
	}, [isCollapsed, showModal]);

	// exposure event is in its own useEFfect cos if we bundle it with the showing of the flag, it will fire twice
	// once when the useEffect triggers and then again when the flag is shown
	useEffect(() => {
		isUpgradedInCurrentSession &&
			fireUpgradeOnboardingExperimentExposure(createAnalyticsEvent, projectType, 'flag');
	}, [createAnalyticsEvent, isUpgradedInCurrentSession, projectType]);

	useEffect(() => {
		isVariation && isUpgradedInCurrentSession && showFlag();
	}, [isVariation, isUpgradedInCurrentSession, showFlag]);

	useEffect(() => {
		(async () => {
			// If we are already in standard, check EP if we were just recently upgraded
			if (
				!isUpgradedInCurrentSession &&
				editionFromTenantContext === STANDARD_EDITION &&
				atlassianAccountId
			) {
				const hasUpgradedTraits = await isUpgradedBasedOnTraits(
					product,
					cloudId,
					transactionAccountId,
				);

				if (hasUpgradedTraits) {
					const wasModalViewed = await getModalViewedProperty(
						createAnalyticsEvent,
						projectType,
						atlassianAccountId,
					);

					// we only check EP message if we have the traits set and
					// the modal hasn't been shown before based on the user property
					if (!wasModalViewed) {
						setEpMessageStarted();
					}
				}
			}
		})();
	}, [
		atlassianAccountId,
		cloudId,
		createAnalyticsEvent,
		editionFromTenantContext,
		isUpgradedInCurrentSession,
		product,
		projectType,
		setEpMessageStarted,
		transactionAccountId,
	]);

	const {
		shouldShow: shouldShowSpotlightOnLand,
		onModalViewed,
		onModalDismissed,
		onClose: onSpotlightClose,
	} = useShouldShowSpotlightOnLand(projectType);

	// exposure event is in its own useEFfect cos if we bundle it with the showing of the modal, it will fire twice
	// once when the useEffect triggers and then again when the modal is shown
	useEffect(() => {
		// fire exposure event for both control and variation
		isEligible &&
			fireUpgradeOnboardingExperimentExposure(createAnalyticsEvent, projectType, 'modal');
	}, [createAnalyticsEvent, isEligible, projectType]);

	useEffect(() => {
		// only show modal for variation
		showModal && onModalViewed();
	}, [onModalViewed, showModal]);

	const showSpotlight = isVariation && (isSpotlightActive || shouldShowSpotlightOnLand);

	return (
		<>
			{showModal && (
				<UpgradeOnboardingModal
					onDismiss={() => {
						onModalDismissed();
						setIsSpotlightActive(true);
					}}
					onClose={() => stopMessage()}
					addPaymentMethodRef={addPaymentMethodRef}
					projectType={projectType}
					transactionAccountId={transactionAccountId}
				/>
			)}
			{showSpotlight && (
				<SpotlightPill
					onClose={() => {
						onSpotlightClose();
						setIsSpotlightActive(false);
					}}
				/>
			)}
		</>
	);
};
